import { extractApiErrorMessage, extractValidationErrors } from '../../../Utils/apiErrorUtils';
import type { EntityFormApiErrorExtractionResult } from '../../Shared/Hooks/useEntityForm.types';

const COUNTRY_FIELD_LABELS: Record<string, string> = {
  name: 'Nazwa kraju',
  description: 'Opis',
  published: 'Publikacja',
};

const GENERIC_VALIDATION_MESSAGE =
  'Formularz zawiera błędy. Popraw oznaczone pola i spróbuj ponownie.';
const GENERIC_SERVER_ERROR_MESSAGE =
  'Wystąpił nieoczekiwany błąd serwera. Spróbuj ponownie za chwilę.';

const normalizeApiFieldPath = (fieldPath: string): string => {
  return fieldPath
    .replace(/^\$?\./, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((segment) => {
      if (!segment) {
        return segment;
      }

      return segment.charAt(0).toLowerCase() + segment.slice(1);
    })
    .join('.');
};

const resolveFieldLabel = (fieldPath: string): string => {
  const normalized = normalizeApiFieldPath(fieldPath);
  return COUNTRY_FIELD_LABELS[normalized] ?? normalized;
};

const translateCountryMessage = (message: string, fieldPath: string): string => {
  const trimmed = message.trim();
  const fieldLabel = resolveFieldLabel(fieldPath);

  if (/^The .* field is required\.?$/i.test(trimmed)) {
    return `Pole "${fieldLabel}" jest wymagane.`;
  }

  if (
    /^The value '.*' is (?:invalid|not valid for).*/i.test(trimmed) ||
    /could not be converted to/i.test(trimmed)
  ) {
    return `Pole "${fieldLabel}" ma nieprawidłową wartość.`;
  }

  const maxLengthMatch = trimmed.match(/maximum length of '(\d+)'/i);
  if (maxLengthMatch) {
    return `Pole "${fieldLabel}" może mieć maksymalnie ${maxLengthMatch[1]} znaków.`;
  }

  return trimmed;
};

const getErrorStatus = (error: unknown): number | string | null => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return null;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return null;
};

const mapGeneralMessage = (message: string, status: number | string | null): string => {
  const trimmed = message.trim();

  if (typeof status === 'number' && status >= 500) {
    return GENERIC_SERVER_ERROR_MESSAGE;
  }

  if (
    /^One or more validation errors occurred\.?$/i.test(trimmed) ||
    /The JSON value could not be converted/i.test(trimmed) ||
    /^Bad Request$/i.test(trimmed)
  ) {
    return GENERIC_VALIDATION_MESSAGE;
  }

  if (
    /^An unexpected error occurred\.?$/i.test(trimmed) ||
    /^Internal Server Error$/i.test(trimmed)
  ) {
    return GENERIC_SERVER_ERROR_MESSAGE;
  }

  return trimmed;
};

export const extractCountryEntityFormApiErrors = (
  error: unknown,
  fallbackMessage: string
): EntityFormApiErrorExtractionResult => {
  const validationErrors = extractValidationErrors(error);
  const fieldErrors: Record<string, string> = {};

  if (validationErrors) {
    Object.entries(validationErrors).forEach(([fieldName, messages]) => {
      const firstMessage = Array.isArray(messages) ? messages[0] : undefined;
      if (!firstMessage) {
        return;
      }

      const normalizedFieldName = normalizeApiFieldPath(fieldName);
      fieldErrors[normalizedFieldName] = translateCountryMessage(firstMessage, fieldName);
    });
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      submitErrorMessage: GENERIC_VALIDATION_MESSAGE,
      fieldErrors,
    };
  }

  const apiMessage = extractApiErrorMessage(error, fallbackMessage);
  const status = getErrorStatus(error);
  return {
    submitErrorMessage: mapGeneralMessage(apiMessage || fallbackMessage, status),
    fieldErrors,
  };
};

